import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Button, Spin, Tooltip, Tag, Modal, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayout from '../../../../components/Layout/StandardLayout';
import AudienceBuilder from '../../../shared/AudienceBuilder';
import AudiencePreviewModal from '../../../shared/AudienceBuilder/AudiencePreviewModal';
import MassMessageForm from '../../../shared/MassMessageForm';
import { MASS_MESSAGE_TEMPLATE_BUTTONS } from '../../../shared/MassMessageForm/constants';
import { useAudienceCount } from '../../../../hooks/query/useAudiences';
import { useMerchantTags } from '../../../../hooks/query/useEvents';
import { useMerchantStores } from '../../../../hooks/query/useStores';
import {
  useMassMessage,
  useUpdateMassMessage,
  useCancelMassMessage,
  useArchiveMassMessage,
} from '../../../../hooks/query/useMassMessages';
import { getMassMessageAudience, getMassMessageErrorMessage } from '../../../../services/massMessages';
import formatPhoneNumber from '../../../../utils/formatPhoneNumber';
import { formatMessagePreview, formatDateTime } from './utils';
import './styles.scss';

const EMPTY_CONFIG = { version: 1, operator: 'AND', tags: { included: [] } };

const STATUS_CONFIG = {
  SCHEDULED: { color: 'blue', label: 'Scheduled' },
  PROCESSING: { color: 'processing', label: 'Processing' },
  PAUSED: { color: 'warning', label: 'Paused' },
  COMPLETED: { color: 'success', label: 'Completed' },
  CANCELING: { color: 'warning', label: 'Canceling' },
  CANCELED: { color: 'error', label: 'Canceled' },
  CANCELLED: { color: 'error', label: 'Canceled' },
  DELETED: { color: 'error', label: 'Deleted' },
};

const DELETABLE = new Set(['SCHEDULED', 'PROCESSING', 'PAUSED']);

const CONTACT_METHOD_LABELS = { EMAIL: 'Email', SMS: 'SMS' };

// ─── Read-only details view ─────────────────────────────────────────────────

const DetailsView = ({ msg }) => {
  const status = (msg.status || '').toUpperCase();
  const statusCfg = STATUS_CONFIG[status] || { color: 'default', label: status };
  const audienceCount = msg.audience?.audienceCount;
  const config = msg.audience?.configuration;
  const isProcessing = status === 'PROCESSING' || status === 'PAUSED';

  const statusTooltip = (() => {
    if (status === 'COMPLETED' && msg.completedDateTime) {
      return `Completed ${formatDateTime(msg.completedDateTime, msg.timezone)}`;
    }
    if ((status === 'CANCELED' || status === 'CANCELLED') && msg.cancelledDateTime) {
      return `Cancelled ${formatDateTime(msg.cancelledDateTime, msg.timezone)}`;
    }
    return null;
  })();

  // Resolve tag/store names for filter display
  const { data: tagsData } = useMerchantTags();
  const { data: storesData } = useMerchantStores();
  const tags = tagsData?.results || [];
  const stores = storesData?.results || storesData?.data || [];

  const tagMap = useMemo(() => {
    const map = {};
    tags.forEach(t => { map[t.id] = t.name; });
    return map;
  }, [tags]);

  const storeMap = useMemo(() => {
    const map = {};
    stores.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [stores]);

  return (
    <div className="mass-message-details">
      {/* Message card */}
      <div className="mass-message-details__section">
        <h3 className="mass-message-details__section-heading">Message</h3>
        <div className="mass-message-details__message-card">
          <div className="mass-message-details__message-text">{formatMessagePreview(msg.message)}</div>
          {msg.attachments?.length > 0 && (
            <div className="mass-message-details__attachments">
              {msg.attachments.map(att => (
                <img
                  key={att.id || att.fileUrl}
                  src={att.fileUrl}
                  alt="Attachment"
                  className="mass-message-details__attachment-img"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 4-column info grid */}
      <div className="mass-message-details__grid">
        {/* Status column */}
        <div className="mass-message-details__grid-col">
          <h4 className="mass-message-details__grid-heading">
            <FontAwesomeIcon icon={['far', 'check']} style={{ marginRight: 6 }} />
            Status
          </h4>
          <Tooltip title={statusTooltip}>
            <Tag color={statusCfg.color} className="mass-message-details__status-tag">
              {statusCfg.label}
              {isProcessing && (
                <FontAwesomeIcon icon={['far', 'spinner-third']} spin style={{ marginLeft: 6 }} />
              )}
            </Tag>
          </Tooltip>
          <dl className="mass-message-details__dl">
            <dt>Audience Size</dt>
            <dd>{audienceCount != null ? audienceCount.toLocaleString() : '—'}</dd>
            <dt>Total Sent</dt>
            <dd>
              {msg.totalSent != null ? msg.totalSent.toLocaleString() : '0'}
              {isProcessing && (
                <FontAwesomeIcon icon={['far', 'spinner-third']} spin style={{ marginLeft: 6, color: '#3aa4fc' }} />
              )}
            </dd>
          </dl>
        </div>

        {/* Scheduled column */}
        <div className="mass-message-details__grid-col">
          <h4 className="mass-message-details__grid-heading">
            <FontAwesomeIcon icon={['far', 'calendar-alt']} style={{ marginRight: 6 }} />
            Scheduled
          </h4>
          <dl className="mass-message-details__dl">
            <dt>Scheduled</dt>
            <dd>{formatDateTime(msg.scheduledDateTime, msg.timezone) || '—'}</dd>
            {msg.startedDateTime ? (
              <>
                <dt>Started</dt>
                <dd>{formatDateTime(msg.startedDateTime, msg.timezone)}</dd>
              </>
            ) : status !== 'SCHEDULED' ? (
              <>
                <dt>Started</dt>
                <dd>Not Started Yet</dd>
              </>
            ) : null}
            {msg.completedDateTime && (
              <>
                <dt>Completed</dt>
                <dd>{formatDateTime(msg.completedDateTime, msg.timezone)}</dd>
              </>
            )}
            {msg.cancelledByName && (
              <>
                <dt>Cancelled By</dt>
                <dd>{msg.cancelledByName}</dd>
              </>
            )}
          </dl>
        </div>

        {/* Details column */}
        <div className="mass-message-details__grid-col">
          <h4 className="mass-message-details__grid-heading">
            <FontAwesomeIcon icon={['far', 'info-circle']} style={{ marginRight: 6 }} />
            Details
          </h4>
          <dl className="mass-message-details__dl">
            <dt>Created By</dt>
            <dd>{msg.associate?.name || msg.createdByName || '—'}</dd>
            <dt>Created Time</dt>
            <dd>{formatDateTime(msg.createdDt, msg.timezone) || '—'}</dd>
            {msg.eventId && (
              <>
                <dt>Event</dt>
                <dd>
                  <Link to={`/Events/event-details/${msg.eventId}`} className="mass-messaging-2__event-link">
                    <FontAwesomeIcon icon={['far', 'calendar-star']} style={{ marginRight: 4 }} />
                    View Event
                  </Link>
                </dd>
              </>
            )}
          </dl>
        </div>

        {/* Filter column */}
        {config && (
          <div className="mass-message-details__grid-col">
            <h4 className="mass-message-details__grid-heading">
              <FontAwesomeIcon icon={['far', 'filter']} style={{ marginRight: 6 }} />
              Filter
            </h4>
            <dl className="mass-message-details__dl">
              {config.tags?.included?.length > 0 && (
                <>
                  <dt>Tags</dt>
                  <dd>{config.tags.included.map(id => tagMap[id] || `Tag ${id}`).join(', ')}</dd>
                </>
              )}
              {config.tags?.excluded?.length > 0 && (
                <>
                  <dt>Excluded Tags</dt>
                  <dd>{config.tags.excluded.map(id => tagMap[id] || `Tag ${id}`).join(', ')}</dd>
                </>
              )}
              {config.stores?.included?.length > 0 && (
                <>
                  <dt>Stores</dt>
                  <dd>{config.stores.included.map(id => storeMap[id] || `Store ${id}`).join(', ')}</dd>
                </>
              )}
              {config.contactMethods?.length > 0 && (
                <>
                  <dt>Contact Methods</dt>
                  <dd>{config.contactMethods.map(m => CONTACT_METHOD_LABELS[m] || m).join(', ')}</dd>
                </>
              )}
              {config.lastContacted && (
                <>
                  <dt>Last Contacted</dt>
                  <dd>
                    {config.lastContacted.mode === 'never' && 'Never'}
                    {config.lastContacted.mode === 'any' && 'Has been contacted'}
                    {config.lastContacted.mode === 'range' && (
                      <>
                        {config.lastContacted.negate && (
                          <>
                            Not contacted within range
                            <br />
                          </>
                        )}
                        {config.lastContacted.from && `From: ${config.lastContacted.from}`}
                        {config.lastContacted.from && config.lastContacted.to && <br />}
                        {config.lastContacted.to && `To: ${config.lastContacted.to}`}
                      </>
                    )}
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}
      </div>

    </div>
  );
};

// ─── Edit mode (SCHEDULED) ──────────────────────────────────────────────────

const EditView = ({ msg, messageId, formRef }) => {
  const history = useHistory();
  const updateMutation = useUpdateMassMessage();
  const audienceCountMutation = useAudienceCount();

  const [audienceConfig, setAudienceConfig] = useState(EMPTY_CONFIG);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && msg.audience?.configuration) {
      const config = msg.audience.configuration;
      setAudienceConfig(config);
      setInitialized(true);

      const hasFilters =
        config?.tags?.included?.length > 0 ||
        config?.tags?.excluded?.length > 0 ||
        config?.stores?.included?.length > 0 ||
        config?.contactMethods?.length > 0 ||
        !!config?.lastContacted;

      if (hasFilters) {
        audienceCountMutation.mutate(config);
      }
    }
  }, [msg, initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const audienceCount = audienceCountMutation.data?.total ?? audienceCountMutation.data?.count ?? 0;
  const isCountLoading = audienceCountMutation.isLoading;

  const handleAudienceChange = useCallback(
    newConfig => {
      setAudienceConfig(newConfig);
      const hasFilters =
        newConfig?.tags?.included?.length > 0 ||
        newConfig?.tags?.excluded?.length > 0 ||
        newConfig?.stores?.included?.length > 0 ||
        newConfig?.contactMethods?.length > 0 ||
        !!newConfig?.lastContacted;

      if (hasFilters) {
        audienceCountMutation.mutate(newConfig);
      } else {
        audienceCountMutation.reset();
      }
    },
    [audienceCountMutation],
  );

  const hasAudienceFilters = useCallback(
    () =>
      audienceConfig?.tags?.included?.length > 0 ||
      audienceConfig?.tags?.excluded?.length > 0 ||
      audienceConfig?.stores?.included?.length > 0 ||
      audienceConfig?.contactMethods?.length > 0 ||
      !!audienceConfig?.lastContacted,
    [audienceConfig],
  );

  const handleFormSubmit = async formData => {
    if (!hasAudienceFilters()) {
      message.error('Please add at least one audience filter');
      return;
    }

    const payload = {
      ...formData,
      audience: { configuration: audienceConfig },
    };

    try {
      await updateMutation.mutateAsync({ massMessageId: messageId, data: payload });
      message.success('Message updated successfully');
      history.push('/Messaging/mass-messaging');
    } catch (error) {
      console.error('Error updating mass message:', error);
      message.error(getMassMessageErrorMessage(error, 'Failed to update message. Please try again.'));
    }
  };

  const audienceSection = (
    <div className="new-mm__section">
      <h3 className="new-mm__section-title">
        <FontAwesomeIcon icon={['far', 'users']} style={{ marginRight: 8 }} />
        AUDIENCE
      </h3>
      <div className="new-mm__audience-row">
        <div className="new-mm__audience-builder">
          <AudienceBuilder
            configuration={audienceConfig}
            onChange={handleAudienceChange}
            audienceCount={audienceCount}
            isCountLoading={isCountLoading}
          />
        </div>
        <div className="new-mm__audience-info">
          <div className="new-mm__audience-count">
            {isCountLoading ? (
              <Spin size="small" />
            ) : (
              <span className="new-mm__audience-count-number">{audienceCount.toLocaleString()}</span>
            )}
            <span className="new-mm__audience-count-label">recipients</span>
          </div>
          {audienceCount > 0 &&
            (audienceCount > 5000 ? (
              <Tooltip title="Preview is not available for audiences larger than 5,000 clients">
                <span className="new-mm__preview-link new-mm__preview-link--disabled">Preview Audience</span>
              </Tooltip>
            ) : (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <span className="new-mm__preview-link" onClick={() => setShowPreviewModal(true)}>
                Preview Audience
              </span>
            ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MassMessageForm
        ref={formRef}
        audienceCount={audienceCount}
        audienceSection={audienceSection}
        templateButtons={MASS_MESSAGE_TEMPLATE_BUTTONS}
        enableAttachments
        editingMessage={msg}
        onSubmit={handleFormSubmit}
        isSaving={updateMutation.isLoading}
      />
      <AudiencePreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        configuration={audienceConfig}
      />
    </>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const MassMessageDetails = () => {
  const { messageId } = useParams();
  const history = useHistory();
  const formRef = useRef();
  const cancelMutation = useCancelMassMessage();
  const archiveMutation = useArchiveMassMessage();

  // Track status for polling — starts false, updated after first fetch
  const [shouldPoll, setShouldPoll] = useState(false);

  const { data, isLoading } = useMassMessage({
    massMessageId: messageId,
    useQueryOptions: {
      refetchInterval: shouldPoll ? 5000 : false,
      onSuccess: d => {
        const s = (d?.data?.status || d?.status || '').toUpperCase();
        setShouldPoll(s === 'PROCESSING' || s === 'PAUSED');
      },
    },
  });
  const msg = data?.data || data || {};

  const status = (msg?.status || '').toUpperCase();
  const isEditable = status === 'SCHEDULED';

  const handleCancel = () => {
    Modal.confirm({
      title: 'Cancel Delivery',
      content: 'Are you sure you want to cancel this message delivery? This cannot be undone.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      cancelText: 'No, Keep It',
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(messageId);
          message.success('Message delivery canceled');
        } catch {
          message.error('Failed to cancel delivery');
        }
      },
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Message',
      content: 'Are you sure you want to delete this message? This cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Keep It',
      onOk: async () => {
        try {
          await archiveMutation.mutateAsync(messageId);
          message.success('Message deleted');
          history.push('/Messaging/mass-messaging');
        } catch {
          message.error('Failed to delete message');
        }
      },
    });
  };

  const breadcrumbs = [
    { title: 'Messaging' },
    { title: 'Mass Messaging', to: '/Messaging/mass-messaging' },
    { title: isEditable ? 'Edit Message' : 'Message Details' },
  ];

  if (isLoading) {
    return (
      <StandardLayout
        title="Message Details"
        backLink="/Messaging/mass-messaging"
        breadCrumbsAboveTitle={breadcrumbs}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </StandardLayout>
    );
  }

  const isProcessing = status === 'PROCESSING' || status === 'PAUSED';

  const headerActions = isEditable ? (
    <Button type="primary" onClick={() => formRef.current?.submit()}>
      Save Changes
    </Button>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {isProcessing && (
        <Button size="small" danger ghost onClick={handleCancel} loading={cancelMutation.isLoading}>
          Cancel Delivery
        </Button>
      )}
      {DELETABLE.has(status) && (
        <Button
          size="small"
          type="text"
          danger
          onClick={handleDelete}
          loading={archiveMutation.isLoading}
          icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
        />
      )}
    </div>
  );

  return (
    <StandardLayout
      title={isEditable ? 'Edit Scheduled Message' : 'Message Details'}
      backLink="/Messaging/mass-messaging"
      breadCrumbsAboveTitle={breadcrumbs}
      actions={headerActions}
    >
      {isEditable ? (
        <EditView msg={msg} messageId={messageId} formRef={formRef} />
      ) : (
        <DetailsView msg={msg} />
      )}
    </StandardLayout>
  );
};

export default MassMessageDetails;
